import type { CSSProperties, ReactNode } from 'react';
import { cn } from '../cn';

export type PreviewBackground = 'chroma' | 'black' | 'transparent_preview' | 'boot';

export interface CaptionMockStyle {
  fontFamily: string;
  fontWeight: number;
  /** px at 1920 wide — converted to cqw. */
  fontSizePx: number;
  color: string;
  uppercase?: boolean;
  /** letter spacing in 1/100 em (design "tracking"). */
  tracking?: number;
  outline?: boolean;
  box: 'none' | 'band' | 'per_line';
  boxOpacity: number;
  /** anchor */
  v: 'top' | 'middle' | 'bottom';
  h: 'left' | 'center' | 'right';
  /** safe margin and offsets in px at 1920 wide. */
  marginPx: number;
  offsetXPx: number;
  offsetYPx: number;
  lines: string[];
  lineHeight?: number;
  textAlign?: 'left' | 'center' | 'right';
  italic?: boolean;
  /** Outline (stroke) around the glyphs; px at 1920 wide. */
  outlineStyle?: { color: string; width: number };
  /** Drop shadow behind the glyphs; px at 1920 wide, opacity 0-1. */
  textShadow?: { color: string; blur: number; x: number; y: number; opacity: number };
  /** Box colour (hex) with `boxOpacity`; paddings / radius in px at 1920 wide. */
  boxColor?: string;
  boxPaddingX?: number;
  boxPaddingY?: number;
  boxRadius?: number;
  boxShadow?: { color: string; blur: number; x: number; y: number; opacity: number };
}

export interface VideoFramePreviewProps {
  background?: PreviewBackground;
  bootColor?: string;
  /** Live MJPEG / JPEG frame. When set, the CSS caption mock is not drawn. */
  src?: string | null;
  mock?: CaptionMockStyle;
  showSafeArea?: boolean;
  /** Overlay slots: mono 10px white .75 (9px on mobile). */
  topLeft?: ReactNode;
  topRight?: ReactNode;
  bottomRight?: ReactNode;
  cornerMarks?: boolean;
  className?: string;
}

const FAMILY_STACK: Record<string, string> = {
  Geist: "'Geist', 'Geist Sans', sans-serif",
  'Geist Mono': "'Geist Mono', monospace",
  'Atkinson Hyperlegible': "'Atkinson Hyperlegible', sans-serif",
  'IBM Plex Sans': "'IBM Plex Sans', sans-serif",
  'Helvetica Neue': "'Helvetica Neue', Helvetica, Arial, sans-serif",
};

const cq = (px: number) => `${(px / 1920) * 100}cqw`;

/**
 * 16:9 frame with container queries so the caption mock scales with the frame width (cqw = 1 % of 1920).
 * Design: radius 10, 1 px line2 border, 10 px corner marks, overlay labels at 12/28 (mobile 10/12), dashed safe area.
 */
export function VideoFramePreview({
  background = 'chroma',
  bootColor = '#00B140',
  src,
  mock,
  showSafeArea,
  topLeft,
  topRight,
  bottomRight,
  cornerMarks = true,
  className,
}: VideoFramePreviewProps) {
  const bg =
    background === 'boot'
      ? bootColor
      : background === 'chroma'
        ? bootColor
        : background === 'black'
          ? '#000000'
          : 'var(--cap-alpha-preview)';
  const m = mock ? cq(mock.marginPx) : '0px';
  return (
    <div
      className={cn('cq relative w-full overflow-hidden rounded-xl border border-line2', className)}
      style={{ aspectRatio: '16 / 9', background: bg }}
    >
      {src && (
        <img src={src} alt="" className="absolute inset-0 h-full w-full object-contain" draggable={false} />
      )}
      {cornerMarks && (
        <>
          <span className="absolute left-2.5 top-2.5 h-2.5 w-2.5 border-l border-t border-white/55" />
          <span className="absolute right-2.5 top-2.5 h-2.5 w-2.5 border-r border-t border-white/55" />
          <span className="absolute bottom-2.5 left-2.5 h-2.5 w-2.5 border-b border-l border-white/55" />
          <span className="absolute bottom-2.5 right-2.5 h-2.5 w-2.5 border-b border-r border-white/55" />
        </>
      )}
      {topLeft && <span className={overlayCls('left-3 top-2.5 md:left-7 md:top-3')}>{topLeft}</span>}
      {topRight && <span className={overlayCls('right-3 top-2.5 md:right-7 md:top-3')}>{topRight}</span>}
      {bottomRight && (
        <span className={overlayCls('bottom-2.5 right-3 md:bottom-3 md:right-7 tabular')}>{bottomRight}</span>
      )}
      {mock && showSafeArea && (
        <div
          className="pointer-events-none absolute border border-dashed border-white/45"
          style={{ left: m, right: m, top: m, bottom: m }}
        />
      )}
      {mock && !src && <CaptionMock mock={mock} />}
    </div>
  );
}

const overlayCls = (pos: string) =>
  cn('absolute font-mono text-2xs font-medium tracking-micro text-white/75 md:text-xs', pos);

export function CaptionMock({ mock }: { mock: CaptionMockStyle }) {
  const m = cq(mock.marginPx);
  const alignItems = { left: 'flex-start', center: 'center', right: 'flex-end' }[mock.h];
  const justify = { top: 'flex-start', middle: 'center', bottom: 'flex-end' }[mock.v];
  const boxBg = rgba(mock.boxColor ?? '#000000', mock.boxOpacity);
  const padX = cq(mock.boxPaddingX ?? 16);
  const padY = cq(mock.boxPaddingY ?? 8);
  const radius = cq(mock.boxRadius ?? 6);
  const bs = mock.boxShadow;
  const boxShadow = bs ? `${cq(bs.x)} ${cq(bs.y)} ${cq(bs.blur)} ${rgba(bs.color, bs.opacity)}` : 'none';
  const shadows: string[] = [];
  const o = mock.outlineStyle;
  if (o && o.width > 0) {
    // a ring of shadows reads as a stroke without -webkit-text-stroke's inner bleed
    const w = cq(o.width);
    for (const [dx, dy] of [
      [1, 0],
      [-1, 0],
      [0, 1],
      [0, -1],
      [0.7, 0.7],
      [-0.7, 0.7],
      [0.7, -0.7],
      [-0.7, -0.7],
    ])
      shadows.push(`calc(${w} * ${dx}) calc(${w} * ${dy}) 0 ${o.color}`);
  } else if (mock.outline && !mock.outlineStyle) shadows.push('0 0 0.08em #000');
  const ts = mock.textShadow;
  if (ts) shadows.push(`${cq(ts.x)} ${cq(ts.y)} ${cq(ts.blur)} ${rgba(ts.color, ts.opacity)}`);
  else if (mock.outline && !mock.outlineStyle) shadows.push('0.04em 0.04em 0.1em rgba(0,0,0,.85)');
  const wrap: CSSProperties = {
    position: 'absolute',
    left: m,
    right: m,
    top: m,
    bottom: m,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: justify,
    alignItems,
    transform: `translate(${cq(mock.offsetXPx)}, ${cq(mock.offsetYPx)})`,
  };
  const band: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: { left: 'flex-start', center: 'center', right: 'flex-end' }[mock.textAlign ?? mock.h],
    background: mock.box === 'band' ? boxBg : 'transparent',
    padding: mock.box === 'band' ? `${padY} ${padX}` : 0,
    borderRadius: mock.box === 'band' ? radius : 0,
    boxShadow: mock.box === 'band' ? boxShadow : 'none',
    maxWidth: '100%',
  };
  const line: CSSProperties = {
    font: `${mock.italic ? 'italic ' : ''}${mock.fontWeight} ${cq(mock.fontSizePx)}/${mock.lineHeight ?? 1.3} ${FAMILY_STACK[mock.fontFamily] ?? `'${mock.fontFamily}', 'Helvetica Neue', sans-serif`}`,
    color: mock.color,
    background: mock.box === 'per_line' ? boxBg : 'transparent',
    padding: mock.box === 'per_line' ? `0 ${padX}` : 0,
    borderRadius: mock.box === 'per_line' ? radius : 0,
    boxShadow: mock.box === 'per_line' ? boxShadow : 'none',
    textTransform: mock.uppercase ? 'uppercase' : 'none',
    letterSpacing: `${(mock.tracking ?? 0) / 100}em`,
    textShadow: shadows.length ? shadows.join(', ') : 'none',
    textAlign: mock.textAlign ?? mock.h,
    whiteSpace: 'pre-wrap',
    minHeight: '1.3em',
  };
  return (
    <div style={wrap} aria-hidden>
      <div style={band}>
        {mock.lines.map((t, i) => (
          <div key={i} style={line}>
            {t}
          </div>
        ))}
      </div>
    </div>
  );
}

function rgba(hex: string, alpha: number): string {
  const h = /^#[0-9a-fA-F]{6}$/.test(hex) ? hex.slice(1) : '000000';
  return `rgba(${parseInt(h.slice(0, 2), 16)},${parseInt(h.slice(2, 4), 16)},${parseInt(h.slice(4, 6), 16)},${Math.max(0, Math.min(1, alpha))})`;
}
